/**
 * Spec №12 Stage C + №13.1 (§2.2) — Day view v2 / timesheet.
 *
 * The capture strip + the two №13.1 additions: (1) **matter-context launch** — when
 * opened from a matter (`?matter=MAT-####`, e.g. via a contextual action) the
 * matter is prefilled + LOCKED (a lock chip, not an editable select); (2) the
 * shared **arrangement picker** (§2.7) — one open arrangement auto-attributes, many
 * → a pre-selected dropdown. The inline `preview_rate` snapshot shows the resolved
 * rate + its scope badge (matter/client/firm); a manual override wins. record_time
 * stamps the rate at capture — later rate-card edits never re-price recorded time.
 */
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useMatters, useTimeEntries, useRatePreview, useRecordTime } from "../../hooks/use-time-billing";
import { ArrangementPicker } from "./ArrangementPicker";
import { money } from "../../components/ledger/format";
import { showToast } from "../../components/primitives";
import { formatUnits, parseUnitsInput, useTimeUnitMode } from "../../lib/format-units";

function todayIso(): string { return new Date().toISOString().slice(0, 10); }
const amtOf = (t: Record<string, unknown>): number => Math.round(((t.units as number) ?? 0) * ((t.rate_snapshot_minor as number) ?? 0));

export function DayViewPage() {
  const matters = useMatters();
  const entries = useTimeEntries();
  const record = useRecordTime();
  const [params] = useSearchParams();
  const launchedMatter = params.get("matter") ?? "";

  const [matterId, setMatterId] = useState(launchedMatter);
  const [arrangementId, setArrangementId] = useState("");
  const [resource, setResource] = useState("Solicitor");
  const [narrative, setNarrative] = useState("");
  const [units, setUnits] = useState("");
  const [rateOverride, setRateOverride] = useState("");

  const { mode } = useTimeUnitMode();
  const effMatter = launchedMatter || matterId;
  const preview = useRatePreview(resource, effMatter || undefined);
  const cardRateMinor = preview.data?.rate_minor ?? null;
  const effectiveRateMinor = rateOverride.trim() ? Math.round((parseFloat(rateOverride) || 0) * 100) : (cardRateMinor ?? 0);
  // Entry parses the same way it displays (M29): six-minute = raw units typed;
  // decimal-hours = hours typed, converted to raw units for storage + rate math.
  const unitsNum = parseUnitsInput(units, mode);
  const lineAmount = Math.round(effectiveRateMinor * unitsNum);

  const today = todayIso();
  const todays = useMemo(() => (entries.data ?? []).filter((t) => (t.entry_date as string | undefined) === today), [entries.data, today]);
  const recent = todays.length > 0 ? todays : (entries.data ?? []).slice(0, 12);
  const total = recent.reduce((s, t) => s + amtOf(t), 0);
  const matterName = (id: string): string => (matters.data ?? []).find((m) => m.id === id)?.title as string ?? id;

  async function add() {
    if (!effMatter) { showToast("Pick a matter first", "error"); return; }
    try {
      const r = await record.mutateAsync({
        matter_id: effMatter,
        ...(arrangementId && arrangementId !== "__all__" ? { fee_arrangement_id: arrangementId } : {}),
        resource, units: unitsNum, rate_snapshot_minor: effectiveRateMinor, narrative, billable: "yes", entry_date: today,
      });
      setNarrative(""); setUnits("");
      showToast((r as { created_default_arrangement?: boolean })?.created_default_arrangement ? "Time recorded (opened a General fee arrangement)" : "Time recorded", "success");
    } catch (e) {
      showToast((e as Error).message, "error");
    }
  }

  return (
    <div className="screen vt">
      <div className="surface" style={{ marginBottom: 0 }}>
        <div className="surface__bar"><div className="tb-daybar">
          <span className="statutory">{new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</span>
          <span className="target">Today</span>
          <span className="tot money">{money(total)}</span>
        </div></div>
      </div>

      {/* capture */}
      <div className="tb-card" style={{ marginTop: 14 }}>
        <div className="tb-card__h">Capture <span className="sub">rate previews before you commit</span></div>
        <div className="tb-card__b">
          {launchedMatter ? (
            <div className="lockrow" style={{ marginBottom: 10 }}><span className="lk badge badge--soft">matter_id</span><span className="val">{launchedMatter}</span><span className="pad">🔒 launched from the matter</span></div>
          ) : null}
          <div className="picker" style={{ marginBottom: 10 }}>
            {launchedMatter ? null : (
              <select value={matterId} onChange={(ev) => { setMatterId(ev.target.value); setArrangementId(""); }}>
                <option value="">Pick a matter…</option>
                {(matters.data ?? []).map((m) => <option key={m.id} value={m.id}>{(m.title as string) ?? m.id} ({m.id})</option>)}
              </select>
            )}
            {effMatter ? <ArrangementPicker matterId={effMatter} value={arrangementId} onChange={setArrangementId} /> : null}
          </div>
          <div className="tb-capture">
            <input value={resource} onChange={(ev) => setResource(ev.target.value)} aria-label="resource" placeholder="Resource" />
            <input value={narrative} onChange={(ev) => setNarrative(ev.target.value)} placeholder="What did you do?" />
            <input value={units} onChange={(ev) => setUnits(ev.target.value)} placeholder={mode === "decimal_hours" ? "Hours" : "Units"} aria-label={mode === "decimal_hours" ? "hours" : "units"} inputMode="decimal" />
            <span className="tb-rate" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span className="tiny muted">$</span>
              <input className="cem-input" style={{ width: 64, padding: "6px 8px" }} value={rateOverride} onChange={(ev) => setRateOverride(ev.target.value)} placeholder={cardRateMinor != null ? (cardRateMinor / 100).toFixed(0) : "rate"} inputMode="decimal" aria-label="rate per unit (dollars)" />
              <span className="tiny">
                /u{effectiveRateMinor > 0 && unitsNum > 0 ? <> → <b>{money(lineAmount)}</b></> : null}
                <br />
                {cardRateMinor != null ? <span className="badge badge--tm">{preview.data?.scope ?? "rate"} card · {money(cardRateMinor)}</span> : rateOverride.trim() ? "manual · snapshot" : "no card — enter a rate"}
              </span>
            </span>
            <button data-variant="primary" onClick={() => void add()} disabled={record.isPending}>{record.isPending ? "…" : "Add"}</button>
          </div>
          <p className="tiny muted" style={{ margin: "8px 2px 0" }}>Rate resolved from the applicable card (matter &gt; client &gt; firm); the snapshot is stamped on the entry at capture.</p>
        </div>
      </div>

      {/* entries */}
      <div className="tb-card">
        <div className="tb-card__h">Entries <span className="sub">{recent.length} · {money(total)}</span></div>
        <div className="tb-card__b flush">
          {entries.isLoading ? (
            <div className="tb-entry"><div className="skeleton sk-line" /><div className="skeleton sk-line" /><div className="skeleton sk-line" /><div className="skeleton sk-line" /><div className="skeleton sk-line" /><div className="skeleton sk-line" /></div>
          ) : recent.length === 0 ? (
            <div className="empty-state" style={{ padding: 28 }}><h3>No time recorded yet</h3><p>Capture from the strip above. Time is never a gate — you can bill without it.</p></div>
          ) : (
            <>
              <div className="tb-entry head"><span>Date</span><span>Matter</span><span>Narrative</span><span className="amt">{mode === "decimal_hours" ? "Hours" : "Units"}</span><span className="amt">WIP</span><span>Status</span></div>
              {recent.map((t) => (
                <div className="tb-entry" key={t.id as string}>
                  <span className="row-date">{(t.entry_date as string) ?? "—"}</span>
                  <span className="matter" title={matterName(t.matter as string)}>{t.matter as string}</span>
                  <span>{(t.narrative as string) ?? "—"}</span>
                  <span className="amt">{formatUnits((t.units as number) ?? 0, mode)}</span>
                  <span className="amt money">{money(amtOf(t))}</span>
                  <span><span className={`pill ${t.status === "wip" ? "pill--issued" : "pill--pending"}`}><span className="dot" />{(t.status as string) ?? "draft"}</span></span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
